import React, { useState } from 'react';
import { Badge, Button, Form, Modal, Table, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ApiService from '../../../helpers/apiService';

interface User {
  userId: string;
  name: string;
  kind: string;
  start_date: string;
  update_date?: string;
  account_list: { account: string; currency: string; kind: string; type: string }[];
  kyc_data?: { doc_id: string; doc_type: string; number: string; series: string; issue_data: string; exp_date: string; resident: string }[];
  aml_data?: { doc_id: string; status: string; comment: string }[];
  is_manager: boolean;
  is_enabled: boolean;
  data_company?: string[];
  status: string;
  scoring?: { manual_block: boolean };
}

interface UsersTableProps {
  users: User[];
  fetchUsers: () => void;
  limit: number;
  onLimitChange: (limit: number) => void;
}

const STATUS_BADGE: Record<string, string> = {
  active: 'success',
  blocked: 'danger',
  pending: 'secondary',
  inactive: 'secondary',
};

const LIMIT_OPTIONS = [10, 25, 50];

const UsersTable: React.FC<UsersTableProps> = ({ users, fetchUsers, limit, onLimitChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const toggleSelect = (userId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(selectedIds.size === users.length ? new Set() : new Set(users.map(u => u.userId)));
  };

  const handleBulkAction = async (action: 'block' | 'unblock') => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    const apiMethod = action === 'block' ? ApiService.blockUser.bind(ApiService) : ApiService.unblockUser.bind(ApiService);
    try {
      await Promise.all(Array.from(selectedIds).map(id => apiMethod(id, `Массовая ${action === 'block' ? 'блокировка' : 'разблокировка'}`)));
      setSelectedIds(new Set());
      fetchUsers();
    } catch (e) {
      console.error('Ошибка массовой операции:', e);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSwitchToggle = (user: User) => { setSelectedUser(user); setShowModal(true); };

  const handleBlockUnblockUser = async () => {
    if (!selectedUser) return;
    setLoading(true);
    const apiMethod = selectedUser.is_enabled ? ApiService.blockUser.bind(ApiService) : ApiService.unblockUser.bind(ApiService);
    try {
      await apiMethod(selectedUser.userId, `${blockReason}. ${comment}`);
      setShowModal(false);
      setBlockReason('');
      setComment('');
      fetchUsers();
    } catch (e) {
      console.error('Ошибка при блокировке/разблокировке:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d: string) => d === '0001-01-01T00:00:00Z' ? 'N/A' : new Date(d).toLocaleDateString('ru-RU');

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          {selectedIds.size > 0 && (
            <>
              <span className="text-muted small">Выбрано: <strong>{selectedIds.size}</strong></span>
              <Button variant="outline-danger" size="sm" disabled={bulkLoading} onClick={() => handleBulkAction('block')}>
                Заблокировать
              </Button>
              <Button variant="outline-success" size="sm" disabled={bulkLoading} onClick={() => handleBulkAction('unblock')}>
                Разблокировать
              </Button>
            </>
          )}
        </div>
        <div className="d-flex gap-1">
          {LIMIT_OPTIONS.map(opt => (
            <Button key={opt} variant={limit === opt ? 'primary' : 'outline-secondary'} size="sm" onClick={() => onLimitChange(opt)}>
              {opt}
            </Button>
          ))}
        </div>
      </div>

      <Table hover responsive size="sm" className="align-middle" style={{ fontSize: '0.82rem' }}>
        <thead className="table-light">
          <tr>
            <th style={{ width: 40 }}>
              <Form.Check
                type="checkbox"
                checked={users.length > 0 && selectedIds.size === users.length}
                onChange={toggleSelectAll}
                aria-label="Выбрать все"
              />
            </th>
            <th>User ID</th>
            <th>Имя</th>
            <th>Дата создания</th>
            <th>Счета</th>
            <th>Менеджер</th>
            <th>Активен</th>
            <th>Статус</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr><td colSpan={8} className="text-muted text-center py-3">Нет пользователей</td></tr>
          ) : users.map(user => (
            <tr key={user.userId}>
              <td>
                <Form.Check
                  type="checkbox"
                  checked={selectedIds.has(user.userId)}
                  onChange={() => toggleSelect(user.userId)}
                  aria-label={`Выбрать ${user.userId}`}
                />
              </td>
              <td>
                <Link to={`/checks/profile?userId=${user.userId}`} className="text-truncate" style={{ maxWidth: 160 }}>
                  <code style={{ fontSize: '0.75rem' }}>{user.userId}</code>
                </Link>
              </td>
              <td>{user.name.trim() || <span className="text-muted">—</span>}</td>
              <td>{formatDate(user.start_date)}</td>
              <td>
                {user.account_list?.length > 0 ? (
                  <Link to={`/accounts?userId=${user.userId}`}>
                    <Badge bg="secondary" style={{ fontSize: '0.72rem' }}>{user.account_list.length} счёт(а)</Badge>
                  </Link>
                ) : <span className="text-muted">—</span>}
              </td>
              <td>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{user.data_company?.length ? user.data_company.join(', ') : 'Нет компаний'}</Tooltip>}
                >
                  <Form.Check type="switch" checked={user.is_manager} readOnly aria-label="Manager" />
                </OverlayTrigger>
              </td>
              <td>
                <Form.Check type="switch" checked={user.is_enabled} onChange={() => handleSwitchToggle(user)} aria-label="Активен" />
              </td>
              <td>
                {user.status ? (
                  <Badge bg={STATUS_BADGE[user.status] ?? 'light'} text={STATUS_BADGE[user.status] ? undefined : 'dark'} style={{ fontSize: '0.72rem' }}>
                    {user.status}
                  </Badge>
                ) : <span className="text-muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {selectedUser?.is_enabled ? 'Блокировка' : 'Разблокировка'} пользователя
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-12">
              <Form.Label className="small">Причина {selectedUser?.is_enabled ? 'блокировки' : 'разблокировки'}</Form.Label>
              <Form.Select size="sm" value={blockReason} onChange={e => setBlockReason(e.target.value)}>
                <option value="">Выберите причину</option>
                <option value="reason1">Причина 1</option>
                <option value="reason2">Причина 2</option>
              </Form.Select>
            </div>
            <div className="col-12">
              <Form.Label className="small">Комментарий</Form.Label>
              <Form.Control
                size="sm"
                as="textarea"
                rows={3}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Введите описание причины"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)}>Отмена</Button>
          <Button variant="primary" size="sm" onClick={handleBlockUnblockUser} disabled={loading}>
            {loading ? 'Обработка...' : 'Подтвердить'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UsersTable;
